import { selectors as managementSelectors } from 'client/reducers/managementReducer'
import { selectors as pageSelectors } from 'client/reducers/pageReducer'
import typedManagementSelectors from 'client/store/management/managementSelectors'
import { removeLostRulesMiddleware } from 'client/store/middlewares/removeLostRulesMiddleware'
import typedPageSelectors from 'client/store/page/pageSelectors'
import typedPaymentSelectors from 'client/store/payment/paymentSelectors'
import typedReduxLoggingSelectors from 'src/editor/store/redux-logging/redux-logging-selectors'
import { BookingCalendarSteps } from 'common/components/entities/Calendar/constants'
import { localeValues } from 'common/constants/localeTypes'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { PaymentFormStepTypeEnum } from 'common/enums/PaymentFormStepTypeEnum'
import { BaseEntityInterface } from 'common/types/entities/EntityInterface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import {
  DigitalProductInterface,
  ProductInterface,
} from 'common/types/entities/ProductInterface'
import { OfferInterface, OneShotPlan } from 'common/types/OfferInterface'
import {
  GlobalSettingsInterface,
  SeoInterface,
  Tracking,
} from 'common/types/Page'
import { ValueOf } from 'common/types/utilityTypes'
import { Dispatch } from 'react'
import { shallowEqual, TypedUseSelectorHook, useSelector } from 'react-redux'
import {
  AnyAction,
  applyMiddleware,
  combineReducers,
  compose,
  legacy_createStore as createStore,
  Reducer,
} from 'redux'
import { thunk } from 'redux-thunk'
import { WebsiteLoginState } from 'src/editor/store/website-login/website-login-reducer'
import { WebsiteSettingsActionType } from 'src/editor/store/website-settings/website-settings-reducer'
import { SidebarTabEnum } from 'src/shared/enums/website-enums'
import { FormElementInterface } from 'src/shared/types/entities/form-interface'
import { WebsiteSettingsState } from 'src/shared/types/website-settings-types'
import { PaymentMethodEnum } from '../../common/enums/PaymentMethodEnum'
import { reducers as staticReducers } from '../reducers'
import { AutomationRulesActions } from './automationRules/automationRulesActions'
import { AutomationRulesState } from './automationRules/automationRulesReducer'
import { BlogActions } from './blog/blogActions'
import { BlogState, selectors as blogSelectors } from './blog/blogReducer'
import { BlogPostActions } from './blogPost/blogPostActions'
import { BlogPostState } from './blogPost/blogPostReducer'
import { CustomLoginActions } from './custom-login/customLoginActions'
import { CustomLoginState } from './custom-login/customLoginReducer'
import { FilesActions } from './files/filesActions'
import { FilesState } from './files/filesReducer'
import { InputTypesState } from './inputTypes/inputTypesReducer'
import { ManagementActions } from './management/managementActions'
import { PageActions } from './page/pageActions'
import { ReduxLoggingState } from 'src/editor/store/redux-logging/redux-logging-reducer'
import { logRootMasterBlockEntitiesLostMasterBlockIdMiddleware } from './middlewares/root-master-block-entities-lost-master-block-id-without-id-middleware'

export type Entities = Record<string, BaseEntityInterface | OldEntityInterface>

export interface PresentPage {
  id: number
  entities: Entities
  globalSettings: GlobalSettingsInterface
  parentGlobalSettings?: GlobalSettingsInterface
  seo?: SeoInterface
  type: PageTypeEnum
  locale: ValueOf<typeof localeValues>
  isTemplate: boolean
  doubleOptIn: boolean
  lastPopupNumber: number
  lastFormNumber: number
  tracking?: Tracking
}

interface PageState {
  present: PresentPage
}

export interface UserData {
  email: string
  name: string
  id: number
  isAdmin: boolean
  dashboardLocale: ValueOf<typeof localeValues>
  impersonator: {
    id: number
    email: string
    roles: string[]
  } | null
  isFreemium: boolean
  timezone: string
}

export interface ManagementState {
  activeSidebarTab: SidebarTabEnum | null
  editingEntityId: string | null
  customLoginMode?: string
  user: UserData | Partial<UserData>
  settingsEditing: {
    id: string | null
    needOverlay: boolean
  } & (
    | {
        type?: Exclude<EntityTypeEnum, EntityTypeEnum.Form>
        elementId?: never
      }
    | {
        type: EntityTypeEnum.Form
        elementId?: FormElementInterface['id'] | undefined
      }
  )
  needScroll: boolean
  createBlockDialogEntityId: string | null
  isSettingsOverlayVisible: boolean
  isPaypalSettingsOpened: boolean
  exitUrl: string | null
  twoStepPaymentFormStepType: PaymentFormStepTypeEnum
  bookingCalendarStepType: BookingCalendarSteps
  hasLostEntities: boolean
  hasMasterBlockRootWithoutMasterBlockId: boolean
  hasMasterBlockIdsWithoutMasterRoot: boolean
  isSurveyResult: boolean
  entityInnerItemActiveIndex: Record<string, number>
  customColors: string[]
  activePopupId: string | null
  sideMenuOpened: boolean
  dropDownMenuOpened: boolean
  instantUploadEnabled: boolean
  isCodeDialogOpened: false
  isCodeDialogPortalAllowed: false
  hasFacebookConversionPixel?: boolean
  shownSearchResults: string[]
}

export interface PaymentState {
  offer: OfferInterface
  product: ProductInterface | null
  paymentMethods: PaymentMethodEnum[]
  offerBump: OfferInterface<OneShotPlan>
  digitalProduct: DigitalProductInterface
}

export interface RootState {
  readonly page: PageState
  readonly management: ManagementState
  readonly blog: BlogState
  readonly blogPost: BlogPostState
  readonly customLogin: CustomLoginState
  readonly files: FilesState
  readonly inputTypes: InputTypesState
  readonly payment: PaymentState
  readonly automationRules: AutomationRulesState
  readonly websiteSettings: WebsiteSettingsState
  readonly websiteLogin: WebsiteLoginState
  readonly reduxLogging: ReduxLoggingState
}

export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector

export function useManagement<T>(
  selector: (emailState: ManagementState) => T,
): T {
  return useTypedSelector(state => selector(state.management), shallowEqual)
}

export function usePage<T>(selector: (pageState: PresentPage) => T): T {
  return useTypedSelector(state => selector(state.page.present), shallowEqual)
}

export function useBlog<T>(selector: (blogState: BlogState) => T): T {
  return useTypedSelector(state => selector(state.blog), shallowEqual)
}

export function useBlogPost<T>(selector: (blogState: BlogPostState) => T): T {
  return useTypedSelector(state => {
    return state.blogPost && selector(state.blogPost)
  }, shallowEqual)
}

export function useCustomLogin<T>(
  selector: (blogState: CustomLoginState) => T,
): T {
  return useTypedSelector(
    state => state.customLogin && selector(state.customLogin),
    shallowEqual,
  )
}

export function usePayment<T>(selector: (paymentState: PaymentState) => T): T {
  return useTypedSelector(state => selector(state.payment), shallowEqual)
}

export function useFiles<T>(selector: (filesState: FilesState) => T): T {
  return useTypedSelector(state => selector(state.files), shallowEqual)
}

export function useInputTypes<T>(
  selector: (inputTypesState: InputTypesState) => T,
): T {
  return useTypedSelector(state => selector(state.inputTypes), shallowEqual)
}

export function useAutomationRules<T>(
  selector: (automationRulesState: AutomationRulesState) => T,
): T {
  return useTypedSelector(
    state => selector(state.automationRules),
    shallowEqual,
  )
}

export function useWebsiteSettings<T>(
  selector: (websiteSettingsState: WebsiteSettingsState) => T,
): T {
  return useTypedSelector(
    state => selector(state.websiteSettings),
    shallowEqual,
  )
}

export function useWebsiteLogin<T>(
  selector: (websiteLoginState: WebsiteLoginState) => T,
): T {
  return useTypedSelector(
    state => state.websiteLogin && selector(state.websiteLogin),
    shallowEqual,
  )
}

export function useReduxLogging<T>(
  selector: (reduxLoggingState: ReduxLoggingState) => T,
): T {
  return useTypedSelector(state => selector(state.reduxLogging), shallowEqual)
}

const composeEnhancers =
  (typeof window !== 'undefined' &&
    // biome-ignore lint/suspicious/noExplicitAny: migrated from eslint
    (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose

const enhancer = applyMiddleware(
  thunk,
  removeLostRulesMiddleware,
  logRootMasterBlockEntitiesLostMasterBlockIdMiddleware,
)

// interface AppStore extends Store {
//   asyncReducers: Record<string, Reducer>
//   injectReducer: (key: string, asyncReducer: any) => void
// }

export function createReducerManager(initialReducers: Record<string, Reducer>) {
  // Create an object which maps keys to reducers
  const reducers = { ...initialReducers }

  // Create the initial combinedReducer
  let combinedReducer = combineReducers(reducers)

  // An array which is used to delete state keys when reducers are removed
  let keysToRemove: string[] = []

  return {
    getReducerMap: () => reducers,

    // The root reducer function exposed by this object
    // This will be passed to the store
    // biome-ignore lint/suspicious/noExplicitAny: migrated from eslint
    reduce: (state: Record<string, any>, action: AnyAction) => {
      // If any reducers have been removed, clean up their state first
      if (keysToRemove.length > 0) {
        state = { ...state }
        for (const key of keysToRemove) {
          delete state[key]
        }
        keysToRemove = []
      }
      // Delegate to the combined reducer
      return combinedReducer(state, action)
    },
    // Adds a new reducer with the specified key
    add: (key: string, reducer: Reducer) => {
      if (!key || reducers[key]) {
        return
      }
      // Add the reducer to the reducer mapping
      reducers[key] = reducer
      // Generate a new combined reducer
      combinedReducer = combineReducers(reducers)
    },
  }
}

export function configureStore() {
  const reducerManager = createReducerManager(staticReducers)

  // Create a store with the root reducer function being the one exposed by the manager.
  const store = createStore(
    // @ts-expect-error @TODO: need a description
    reducerManager.reduce,
    {},
    composeEnhancers(enhancer),
  )

  // Optional: Put the reducer manager on the store so it is easily accessible
  // @ts-expect-error @TODO: need a description
  store.reducerManager = reducerManager

  return store
}

export {
  managementSelectors,
  pageSelectors,
  blogSelectors,
  typedManagementSelectors,
  typedPageSelectors,
  typedPaymentSelectors,
  typedReduxLoggingSelectors,
}

export type RootAction =
  | PageActions
  | ManagementActions
  | FilesActions
  | CustomLoginActions
  | BlogPostActions
  | BlogActions
  | AutomationRulesActions
  | WebsiteSettingsActionType

export type AppDispatch = Dispatch<RootAction>
